import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { generalMusicSchema } from "../../ZodSchemas/MusicSchema";
import GeneralMusicData from "../../components/EditMusic";
import NoSubscription from "../../components/user/NoSubscription";
import {
  uploadFileToMega,
  getMegaConnectionStatus,
} from "../../services/fileStorageService";
import { musicService, transactionService } from "../../services";
import ErrorModal from "../../components/UI/Error";
import Spinner from "../../components/UI/Spinner";

const MusicUpdateForm = () => {
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [checkSub, setCheckSub]= useState(false)
  const [image, setImage] = useState(null);
  const [audio, setAudio] = useState(null);
  const [error, setError] = useState({
    image: false,
    audio: false,
    storage: false,
  });

  const methods = useForm({
    resolver: zodResolver(generalMusicSchema),
  });

  useEffect(()=>{
   async function checkSubStatus(){
try {
  const {status}=await transactionService.mySubStatus("id")
setCheckSub(status)
setPageLoading(false)
} catch (error) {
  setError(true)
}
}
checkSubStatus()
  },[])

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;
  const navigatge = useNavigate();
  const onSubmit = async (data) => {
    setError({ storage: false, image: false, audio: false });

    if (!image) return setError({ ...error, image: true });
    if (!audio) return setError({ ...error, audio: true });
    const megaConnected = getMegaConnectionStatus();
    if (!megaConnected) return setError({ ...error, storage: true });

    setLoading(true);

    try {
      const [imageUrl, audioUrl]=await Promise.all([
        uploadFileToMega(image, "cover"),
        uploadFileToMega(audio, "music")
      ])
      if (!imageUrl || imageUrl.error) throw new Error("Image upload failed.");
      setValue("image", imageUrl.nodeId);
      if (!audioUrl || audioUrl.error) throw new Error("Audio upload failed.");
      setValue("audio", audioUrl.nodeId);

      const musicData = {
        ...data,
        image: imageUrl.nodeId,
        audio: audioUrl.nodeId,
      };
      const uploadedMusic = await musicService.uploadMusic(musicData);
      navigatge("/user/my-dashboard/music/all");
    } catch (error) {
      setError({ ...error, storage: true });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setError({ ...error, storage: false });
  };

  if(pageLoading) return <div className="grid place-content-center h-[60vh]"><Spinner/></div>;

  if (!checkSub)
    return (
      <NoSubscription
        title={"No Active Subscription"}
        message={
          "You currently don't have a subscription plan. To enjoy unlimited music features, please choose a plan that suits your vibe."
        }
        buttonText={"Choose a Plan"}
        url={"/plans"}
      />
    );
  return (
    <FormProvider {...methods}>
      <ErrorModal
        isOpen={error.storage}
        onClose={handleCloseModal}
        message={
          error.storage &&
          "Cannot upload your files to our storage now. Please check your connectivities and try again!"
        }
      />
      <form onSubmit={handleSubmit(onSubmit)} className="mb-4">
        <h1 className="text-xl capitalize font-bold mb-4">Upload Music</h1>
        <GeneralMusicData errors={errors} />

        <div className="flex gap-2">
          <div className="w-full">
            <h6 className="text-sm text-gray-400 ml-1 -mb-4 mt-2 p-0 text-left">
              Image
            </h6>
            <input
              type="file"
              className={`${error?.image && "inputError"}`}
              accept="image/*"
              onChange={(e) => {
                setImage(e.target.files[0]);
                setError({ ...error, image: false });
              }}
            />
            {error.image && (
              <p className="formErrorText">Image cannot be empty!</p>
            )}
          </div>

          <div className="w-full">
            <h6 className="text-sm text-gray-400 ml-1 -mb-4 mt-2 p-0 text-left">
              Audio
            </h6>
            <input
              type="file"
              className={`${error?.audio && "inputError"}`}
              accept="audio/*"
              onChange={(e) => {
                setAudio(e.target.files[0]);
                setError({ ...error, audio: false });
              }}
            />
            {error.audio && (
              <p className="formErrorText">Audio cannot be empty!</p>
            )}
          </div>
        </div>

        <button type="submit" className="formSubmitBtn" disabled={loading}>
          {loading ? "Uploading..." : "Upload Music"}
        </button>
      </form>
    </FormProvider>
  );
};

export default MusicUpdateForm;
